/**
 * Browser-side LaTeX -> PDF compilation, backed by a single shared pdfTeX
 * engine. The engine is loaded lazily on first use and reused for the life of
 * the page; compiles are serialized because the worker handles one job at a
 * time.
 */
import { PdfTeXEngine, type CompileResult } from './PdfTeXEngine';

/**
 * Where the engine fetches its format file and TeX packages: the self-hosted
 * TeX Live subset under this site's own `/texlive/`. Resolved against the
 * page URL so the site works from a subpath as well as a root domain. The
 * global override lets the harvest tooling point the engine at a local
 * TeX Live mirror instead.
 */
function texliveEndpoint(): string {
  const override = (globalThis as { __TEXLIVE_ENDPOINT__?: string }).__TEXLIVE_ENDPOINT__;
  return override ?? new URL('texlive/', window.location.href).href;
}

let engine: PdfTeXEngine | undefined;
let loading: Promise<PdfTeXEngine> | undefined;
let queue: Promise<unknown> = Promise.resolve();

async function getEngine(): Promise<PdfTeXEngine> {
  if (engine) return engine;
  loading ??= (async () => {
    const e = new PdfTeXEngine();
    await e.loadEngine();
    e.setTexliveEndpoint(texliveEndpoint());
    engine = e;
    return e;
  })().catch((err: unknown) => {
    loading = undefined;
    throw err;
  });
  return loading;
}

/** Run `fn` after any in-flight compile completes, so jobs never overlap. */
function enqueue<T>(fn: () => Promise<T>): Promise<T> {
  const run = queue.then(fn, fn);
  // Keep the chain alive even if a job rejects.
  queue = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/**
 * Auxiliary files persist in the worker between runs so that references and
 * tables of contents resolve on the next compile of the same document. A
 * different document class writes incompatible aux macros (letter's
 * `\@mlabel`, for one), so on a class change the stale files are blanked.
 */
const AUX_FILES = ['main.aux', 'main.toc', 'main.lof', 'main.lot', 'main.out'];
let lastDocClass: string | undefined;

function resetAuxOnClassChange(e: PdfTeXEngine, tex: string): void {
  const docClass = /\\documentclass[^\n{]*\{([^}]*)\}/.exec(tex)?.[1];
  if (lastDocClass !== undefined && docClass !== lastDocClass) {
    for (const file of AUX_FILES) e.writeMemFSFile(file, '');
  }
  lastDocClass = docClass;
}

// LaTeX resolves \ref, tables of contents, and outlines from the previous
// pass's aux files, so a first (or changed) document needs another pass.
const RERUN = /Label\(s\) may have changed|Rerun to get |Table widths have changed\. Rerun/;

/**
 * Compile a full LaTeX document to a PDF, rerunning while the log asks for
 * it (capped, like latexmk) so cross-references come out right.
 */
export function compilePdf(tex: string): Promise<CompileResult> {
  return enqueue(async () => {
    const e = await getEngine();
    resetAuxOnClassChange(e, tex);
    e.writeMemFSFile('main.tex', tex);
    e.setEngineMainFile('main.tex');
    let result = await e.compileLaTeX();
    for (let pass = 1; pass < 3 && result.status === 0 && RERUN.test(result.log); pass++) {
      result = await e.compileLaTeX();
    }
    return result;
  });
}

// A minimal document exercising the packages of the starter document, so
// warming it pre-fetches the engine, the format file, and the common
// package/font set.
const WARM_TEX = String.raw`\documentclass[11pt]{article}
\usepackage[T1]{fontenc}
\usepackage[utf8]{inputenc}
\usepackage{amsmath,amssymb,amsthm}
\usepackage{mathtools}
\begin{document}
\(x \in \mathbb{R}\)
\end{document}`;

let prewarmed = false;

/**
 * Load the engine and warm the shared package cache in the background, so the
 * first real compile is fast. Best-effort and idempotent: failures are
 * swallowed (real errors surface when the user compiles).
 */
export function prewarmEngine(): void {
  if (prewarmed) return;
  prewarmed = true;
  const start = () => {
    enqueue(async () => {
      const e = await getEngine();
      e.writeMemFSFile('warm.tex', WARM_TEX);
      e.setEngineMainFile('warm.tex');
      return e.compileLaTeX();
    }).catch(() => undefined);
  };
  if (typeof requestIdleCallback === 'function') requestIdleCallback(start, { timeout: 4000 });
  else setTimeout(start, 1500);
}
